import type { FieldNode } from '@fieldia/core';
import type { ElementFactory } from './chrome';
import { choicesOf, conditionEditor } from './condition-editor';
import type { Designer } from './designer';
import { contains, isSection, nodeOf } from './layout-tree';
import { allSections, findNode } from './page-tree';
import { onTab, setting } from './panel-controls';
import { columnsSetting, labelsSetting, rowsSetting, widthSetting } from './panel-layout';
import { groupStyleSetting } from './panel-look';
import { foldSetting } from './panel-fold';
import { movers, type PropertiesView } from './screen-properties';
import { printSetting } from './print-setting';
import { rolesSetting } from './roles-setting';
import { shownWhileSetting } from './shown-while';
import { workGroupSetting } from './work-text';
import { drawSettings } from './draw-settings';
import { removeWithUndo } from './deleted-toast';

/**
 * A group's settings, on the panel's tabs: its title, whether it folds by
 * it, its place among the others and taking it away (Content); its columns,
 * and in twelfths whether its rows stay full (Layout); and when it shows (Rules). An arrangement — parts side by side —
 * has only its layout.
 */

export function groupProperties(el: ElementFactory, designer: Designer, id: string): PropertiesView {
  const focused = (node: Element) => node.ownerDocument.activeElement === node;
  const w = designer.words.panel;
  const title = el('input', { class: 'fd-input', 'aria-label': w.sectionTitle });
  title.addEventListener('input', () => designer.renameContainer(id, title.value));
  const columns = columnsSetting(el, designer, id);
  const rows = rowsSetting(el, designer, id);
  const labels = labelsSetting(el, designer, id, 'group');
  const width = widthSetting(el, designer, id);
  const style = groupStyleSetting(el, designer, id);
  const fold = foldSetting(el, designer, id);
  const work = workGroupSetting(el, designer, id, true);
  const moves = movers(el, designer, id, [w.moveUp, w.moveDown]);
  const remove = el('button', { type: 'button', class: 'fd-button fd-button-danger' }, w.deleteSection);
  remove.addEventListener('click', () => removeWithUndo(designer, id));
  // When it shows: rules on the fields outside it that hold one of a list, or yes or no.
  const when = conditionEditor(el, designer, id, 'group');
  const showWhen = el('button', { type: 'button', class: 'fd-button fd-button-link fd-q-when' }, w.showOnlyWhen);
  showWhen.addEventListener('click', () => when.start());
  const noRules = el('p', { class: 'fd-properties-hint', hidden: '' }, w.noRulesGroup);
  const roles = rolesSetting(el, designer, id);
  const paper = printSetting(el, designer, id);
  const shownWhile = shownWhileSetting(el, designer, id);
  // On a form of responses: its questions in an order drawn for each person, and how many each is asked.
  const draws = drawSettings(el, designer, id);
  const element = el(
    'div',
    { class: 'fd-props' },
    setting(el, 'content', 'Title', title, { words: w.title }),
    ...fold.rows,
    onTab(el('div', { class: 'fd-props-actions' }, ...moves.buttons, remove), 'content', 'Move or delete'),
    ...columns.rows,
    ...work.rows,
    ...rows.rows,
    ...labels.rows,
    ...width.rows,
    ...style.rows,
    onTab(el('div', { class: 'fd-prop fd-prop-when' }, el('span', { class: 'fd-prop-name' }, w.whenItShows), when.element, showWhen, noRules), 'rules', 'When it shows'),
    roles.element,
    shownWhile.element,
    paper.element,
    onTab(el('div', { class: 'fd-prop' }, draws.element), 'rules', 'Questions in random order')
  );
  return {
    element,
    update(page) {
      const section = nodeOf(page, id);
      if (!isSection(section)) return;
      if (!focused(title)) title.value = section.title ?? '';
      for (const part of [columns, rows, labels, width, style, fold, work]) part.update(page);
      moves.update(page);
      // A page keeps one thing at its top, and a tab its last section.
      const holder = findNode(page, id)?.parent;
      remove.hidden = !holder || holder.children.length === 1;
      const outside = allSections(page).flatMap((s) => s.children.filter((n): n is FieldNode => n.type === 'field' && !contains(page, id, n.id) && choicesOf(page.fields[n.field]) !== null));
      when.update(page, outside, section.invisible);
      roles.update(page);
      shownWhile.update(page);
      paper.update(page);
      draws.update(page);
      showWhen.hidden = !when.element.hidden || !when.canStart();
      noRules.hidden = !when.element.hidden || when.canStart();
    },
  };
}

/** Parts side by side: the columns they take, and how wide they are where they sit. */
export function arrangementProperties(el: ElementFactory, designer: Designer, id: string): PropertiesView {
  const columns = columnsSetting(el, designer, id, designer.words.panel.columns);
  const width = widthSetting(el, designer, id);
  const work = workGroupSetting(el, designer, id, false);
  const element = el('div', { class: 'fd-props' }, ...columns.rows, ...work.rows, ...width.rows);
  return {
    element,
    update(page) {
      if (!isSection(nodeOf(page, id))) return;
      columns.update(page);
      work.update(page);
      width.update(page);
    },
  };
}
